// ============================================================================
// @gs/i18n/config — initialisation i18next monorepo (BLITZ-I18N-FOUNDATION)
// ============================================================================
// Architecture hybride : namespace `common` partage (ce package) + namespace
// par app (apps/<app>/src/i18n/locales/<app>.{fr,en}.json). Design V2
// I18N_GUARDRAILS_GS_APPS (2026-05-11), pilotes V3 : ug (SPA) + api-portail (SSR).
// Decision business David 2026-05-11 : FR+EN obligatoire, toutes les apps
// (succursales ouest canadien BC/AB). Fallback TOUJOURS FR (marche primaire).
//
// FACADE REACT (V1 gel du gabarit, D10) : depuis 2026-08-26, tout ce qui n'a pas
// besoin de React vit dans `./core` (initI18nCore). Ce fichier n'ajoute que ce
// qui est propre au navigateur + React : detection de locale (localStorage /
// navigator), <html lang>, plugin `initReactI18next`, `react.useSuspense`.
// Un serveur SSR importe '@gs/i18n/core' et ne tire NI React NI ce fichier.

import i18next from 'i18next';

import { initI18nCore } from './core';
import type { GsI18nCore } from './core';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './supportedLocales';
import type { SupportedLocale } from './supportedLocales';

/** Cle localStorage de l'override manuel (selecteur Langue GsUserMenu). */
export const LOCALE_STORAGE_KEY = 'gs-locale';

/**
 * Instance i18next rendue par `initI18n` — TYPE NOMME (D15), pendant React de
 * `GsI18nCore`. Meme instance, plugin `initReactI18next` en plus : le nom
 * distinct dit QUELLE porte l'a produite, et surtout il donne a `tsc` un type
 * NOMMABLE depuis '@gs/i18n'. Voir la note TS2742 sur `GsI18nCore` : une app ne
 * nomme plus jamais i18next et n'annote plus jamais son `const i18n`.
 */
export interface GsI18n extends GsI18nCore {}

export interface InitI18nOptions {
  appNamespace: string;
  resources: Record<SupportedLocale, Record<string, Record<string, unknown>>>;
  initialLocale?: SupportedLocale;
  /**
   * Hook futur — si defini, prime sur localStorage. Permet branchement a
   * ug.Users.preferred_locale (multi-company ADR-032) sans refactor.
   */
  getUserPreferredLocale?: () => SupportedLocale | null | undefined;
}

/**
 * Lecteur de la preference de l'usager, retenu a l'init pour que `applyCompanyLocale` juge avec le
 * MEME crochet que la detection initiale (la langue de la compagnie ne passe jamais devant lui).
 */
let readUserPreferredLocale: InitI18nOptions['getUserPreferredLocale'];

export function initI18n(options: InitI18nOptions): GsI18n {
  const { appNamespace, resources, initialLocale, getUserPreferredLocale } = options;
  readUserPreferredLocale = getUserPreferredLocale;

  const i18n = initI18nCore({
    appNamespace,
    resources,
    initialLocale: initialLocale ?? detectInitialLocale(getUserPreferredLocale),
    // React echappe deja cote SPA (le defaut `true` de ./core sert le SSR).
    escapeValue: false,
    use: [initReactI18next],
    extra: { react: { useSuspense: false } },
  });

  syncHtmlLang(i18n.language as SupportedLocale);
  return i18n;
}

/** `fr-CA`, `EN`, `en-CA` -> la langue offerte (`fr` | `en`) ; toute autre valeur -> `null`. */
export function normalizeLocale(raw: string | null | undefined): SupportedLocale | null {
  const primary = (raw ?? '').split('-')[0].trim().toLowerCase();
  return SUPPORTED_LOCALES.includes(primary as SupportedLocale) ? (primary as SupportedLocale) : null;
}

export interface LocaleSources {
  userPreferred?: string | null;
  /** Choix memorise du selecteur de langue (localStorage). */
  stored?: string | null;
  /** Langue par defaut de la compagnie (`ug.CompanyBranding.locale`), connue apres le branding. */
  company?: string | null;
  navigatorLanguage?: string | null;
}

/**
 * L'ordre de detection de la langue initiale (ADR-088 regle 8) : preference de l'usager, choix
 * memorise, langue de la compagnie, `navigator.language`, `fr`. Le choix de l'usager est souverain :
 * la compagnie ne passe devant que le navigateur.
 */
export function resolveInitialLocale(sources: LocaleSources): SupportedLocale {
  return (
    normalizeLocale(sources.userPreferred) ??
    normalizeLocale(sources.stored) ??
    normalizeLocale(sources.company) ??
    normalizeLocale(sources.navigatorLanguage) ??
    DEFAULT_LOCALE
  );
}

function storedLocale(): string | null {
  return typeof window === 'undefined' ? null : window.localStorage.getItem(LOCALE_STORAGE_KEY);
}

function detectInitialLocale(
  getUserPreferredLocale?: () => SupportedLocale | null | undefined,
): SupportedLocale {
  if (typeof window === 'undefined') return normalizeLocale(getUserPreferredLocale?.()) ?? DEFAULT_LOCALE;
  // La compagnie n'est pas encore connue ici (le branding arrive apres le login) :
  // `applyCompanyLocale` reprend la main a sa reception.
  return resolveInitialLocale({
    userPreferred: getUserPreferredLocale?.(),
    stored: storedLocale(),
    navigatorLanguage: window.navigator.language,
  });
}

function syncHtmlLang(locale: SupportedLocale): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = locale;
  }
}

/** Change la langue + persiste (localStorage) + synchronise <html lang>. */
export async function setLocale(locale: SupportedLocale): Promise<void> {
  await i18next.changeLanguage(locale);
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  }
  syncHtmlLang(locale);
}

/**
 * Applique la langue par defaut de la compagnie a la reception du branding — SEULEMENT si l'usager
 * n'a ni preference ni choix memorise. Ne persiste rien (la langue d'une compagnie n'est pas un
 * choix de l'usager : un choix memorise ici la figerait contre un changement de compagnie) et
 * synchronise <html lang>. Rend la langue appliquee, ou `null` si rien n'a change.
 */
export async function applyCompanyLocale(raw: string | null | undefined): Promise<SupportedLocale | null> {
  const company = normalizeLocale(raw);
  if (!company) return null;
  if (normalizeLocale(readUserPreferredLocale?.()) || normalizeLocale(storedLocale())) return null;
  if (normalizeLocale(i18next.language) === company) return null;
  await i18next.changeLanguage(company);
  syncHtmlLang(company);
  return company;
}
